import { useFragment } from "@apollo/client";

import {
  AssessmentSettingsFragment,
  RequirementsFragment,
  TaskFormatFragment,
  TaskFormatFragmentDoc,
  TaskFragment,
  TaskFragmentDoc,
  TaskKind,
} from "@/generated/graphql";
import { resolveTaskRequirements } from "@/ui/task/requirements/from-assessment-settings";

/**
 * Hook to access task requirements from Apollo cache using fragment
 * @param taskId - The task ID to fetch requirements for
 * @returns Requirements fragment data or undefined if not in cache
 */
export function useTaskRequirementsFragment(taskId: string) {
  const { data } = useFragment<TaskFragment>({
    from: {
      id: taskId,
      __typename: "Task",
    },
    fragment: TaskFragmentDoc,
    fragmentName: "Task",
  });

  return data?.requirements;
}

/**
 * Hook to access task format and kind from Apollo cache using fragment
 * @param taskId - The task ID to fetch format data for
 * @returns TaskFormatFragment or null if not in cache
 */
export function useTaskFormatFragment(
  taskId: string
): TaskFormatFragment | null {
  const { data, complete } = useFragment<TaskFormatFragment>({
    from: {
      id: taskId,
      __typename: "Task",
    },
    fragment: TaskFormatFragmentDoc,
    fragmentName: "TaskFormat",
  });

  if (data == null || !complete) return null;
  return data;
}

/**
 * "Active task" = the individual-or-viva task for the assessment's primary
 * marking/editing flow. Group and peer tasks don't have this concept — flows
 * that need them should use `findTaskByKind` directly.
 */

export function effectiveTaskKind(
  settings: Pick<AssessmentSettingsFragment, "vivaEnabled"> | null
): TaskKind {
  return settings?.vivaEnabled == true ? TaskKind.Viva : TaskKind.Individual;
}

export function isVideoQuestionEnabled(taskKind: TaskKind): boolean {
  return taskKind === TaskKind.Viva;
}

export function findTaskByKind<T extends { kind: TaskKind }>(
  tasks: T[],
  kind: TaskKind
): T | null {
  return tasks.find((t) => t.kind === kind) ?? null;
}

/**
 * Finds the active (individual or viva) task without resolving requirements.
 * Use this in marking and moderation flows that only need the instruction sheet.
 */
export function findActiveTask<T extends { kind: TaskKind }>(
  tasks: T[],
  settings: Pick<AssessmentSettingsFragment, "vivaEnabled"> | null
): T | null {
  return findTaskByKind(tasks, effectiveTaskKind(settings));
}

/**
 * Finds the active task and overrides Viva task requirements from
 * AssessmentSettings (the source of truth for Viva). Use this in setup flows
 * (home, settings) that render task requirements to the lecturer.
 */
export function selectActiveTask<
  T extends { kind: TaskKind; requirements: RequirementsFragment },
>(tasks: T[], settings: AssessmentSettingsFragment | null): T | null {
  const task = findTaskByKind(tasks, effectiveTaskKind(settings));
  if (!task) return null;

  const requirements = resolveTaskRequirements(task, settings);
  return requirements === task.requirements ? task : { ...task, requirements };
}
